/**
 * Equal Shared Parenting Page Component
 *
 * Powerfully advocates for enacting 50/50 shared parenting presumption laws
 * in every U.S. state with emotionally compelling design and statistics.
 */

import React from 'react'
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Paper,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Fade,
  Grow,
} from '@mui/material'
import {
  ChildCare,
  HeartBroken,
  TrendingDown,
  AttachMoney,
  CheckCircle,
  Gavel,
} from '@mui/icons-material'
import SEOMetaTags from '@/components/SEOMetaTags'
import { Link } from 'react-router-dom'
import { getSEOConfig, getCanonicalUrl } from '@/utils/seo'
import heroEqualSharedParenting from '@/assets/hero-equal-shared-parenting.webp'

/**
 * Equal Shared Parenting Page Component
 */
const EqualSharedParenting: React.FC = () => {
  // Get SEO configuration for this page
  const seoConfig = getSEOConfig('/issues/equal-shared-parenting')!
  const canonicalUrl = getCanonicalUrl('/issues/equal-shared-parenting')

  const [showStats, setShowStats] = React.useState(false)

  React.useEffect(() => {
    const timer = setTimeout(() => setShowStats(true), 300)
    return () => clearTimeout(timer)
  }, [])

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  return (
    <Box
      component="main"
      role="main"
      sx={{
        bgcolor: '#ffffff',
        color: '#333',
        minHeight: '100vh',
      }}
    >
      {/* SEO Meta Tags */}
      <SEOMetaTags
        title={seoConfig.title}
        description={seoConfig.description}
        canonical={canonicalUrl}
        ogImage={seoConfig.ogImage}
        keywords={seoConfig.keywords}
        noIndex={seoConfig.noIndex}
      />

      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          minHeight: { xs: '70vh', md: '80vh' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'primary.dark',
          color: 'white',
          overflow: 'hidden',
        }}
      >
        {/* Background Image */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 0,
            '&::after': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.75)',
              zIndex: 1,
            },
          }}
        >
          <picture>
            <source srcSet={heroEqualSharedParenting} type="image/webp" />
            <img
              src={heroEqualSharedParenting}
              alt="Families advocating for equal shared parenting rights"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: 'center',
              }}
            />
          </picture>
        </Box>

        {/* Content Overlay */}
        <Container
          maxWidth="lg"
          sx={{
            position: 'relative',
            zIndex: 2,
            textAlign: 'center',
            py: { xs: 8, md: 12 },
          }}
        >
          <Fade in timeout={1000}>
            <Typography
              variant="h1"
              component="h1"
              gutterBottom
              sx={{
                fontWeight: 900,
                fontSize: { xs: '2rem', sm: '3rem', md: '4rem' },
                mb: 3,
                color: '#fff',
                textShadow: '2px 2px 8px rgba(0,0,0,0.3)',
                lineHeight: 1.2,
              }}
            >
              Every Child Deserves Both Parents
              <br />
              <Box
                component="span"
                sx={{ color: 'secondary.main', fontWeight: 900 }}
              >
                But Our Laws Steal Them Away
              </Box>
            </Typography>
          </Fade>

          <Fade in timeout={1500}>
            <Typography
              variant="h5"
              component="p"
              sx={{
                fontSize: { xs: '1rem', md: '1.3rem' },
                mb: 4,
                maxWidth: '900px',
                mx: 'auto',
                color: '#fff',
                lineHeight: 1.7,
              }}
            >
              In the shadow of biased courts, millions of kids lose a
              parent&apos;s love, spiraling into heartbreak. Families shatter.
              Taxpayers foot the bill.{' '}
              <Box component="span" sx={{ color: '#fff', fontWeight: 700 }}>
                It&apos;s time to demand equal shared parenting in EVERY state.
              </Box>
            </Typography>
          </Fade>

          <Fade in timeout={2000}>
            <Box>
              <Button
                type="button"
                variant="contained"
                color="secondary"
                size="large"
                onClick={() => scrollToSection('devastating-reality')}
                aria-label="See the problem - Scroll to statistics section"
                sx={{
                  px: 4,
                  py: 1.5,
                  fontSize: { xs: '1rem', md: '1.125rem' },
                  fontWeight: 600,
                  minWidth: { xs: '100%', sm: '200px' },
                  textTransform: 'none',
                  boxShadow: 3,
                  '&:hover': {
                    boxShadow: 6,
                    transform: 'translateY(-2px)',
                    transition: 'all 0.3s ease',
                  },
                }}
              >
                See the Problem
              </Button>
            </Box>
          </Fade>
        </Container>
      </Box>

      {/* Impact Stats Section */}
      <Container maxWidth="lg" sx={{ py: 8 }} id="devastating-reality">
        <Typography
          variant="h2"
          component="h2"
          align="center"
          gutterBottom
          sx={{
            fontWeight: 800,
            mb: 6,
            color: 'primary.main',
            fontSize: { xs: '2rem', md: '3rem' },
          }}
        >
          The Devastating Reality
        </Typography>

        <Grid container spacing={4}>
          {/* Stat Card 1 */}
          <Grid item xs={12} sm={6} md={4}>
            <Grow in={showStats} timeout={1000}>
              <Card
                sx={{
                  height: '100%',
                  bgcolor: '#fff',
                  color: '#333',
                  border: '1px solid #e0e0e0',
                  transition: 'all 0.4s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 12px 32px rgba(0,0,0,0.15)',
                  },
                }}
              >
                <CardContent sx={{ p: 4, textAlign: 'center' }}>
                  <ChildCare
                    sx={{ fontSize: 70, color: 'primary.main', mb: 2 }}
                  />
                  <Typography
                    variant="h3"
                    component="div"
                    sx={{ fontWeight: 900, color: 'secondary.main', mb: 2 }}
                  >
                    31.7%
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ mb: 2, lineHeight: 1.6, color: '#555' }}
                  >
                    of kids in single-parent homes live in poverty vs. 9.5% in
                    two-parent homes
                    <Box
                      component="sup"
                      sx={{ fontSize: '0.7rem', color: 'primary.main' }}
                    >
                      ¹
                    </Box>
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontStyle: 'italic', color: '#757575', mt: 2 }}
                  >
                    Hunger and fear replace stability.
                  </Typography>
                </CardContent>
              </Card>
            </Grow>
          </Grid>

          {/* Stat Card 2 */}
          <Grid item xs={12} sm={6} md={4}>
            <Grow in={showStats} timeout={1300}>
              <Card
                sx={{
                  height: '100%',
                  bgcolor: '#fff',
                  color: '#333',
                  border: '1px solid #e0e0e0',
                  transition: 'all 0.4s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 12px 32px rgba(0,0,0,0.15)',
                  },
                }}
              >
                <CardContent sx={{ p: 4, textAlign: 'center' }}>
                  <HeartBroken
                    sx={{ fontSize: 70, color: 'primary.main', mb: 2 }}
                  />
                  <Typography
                    variant="h3"
                    component="div"
                    sx={{ fontWeight: 900, color: 'secondary.main', mb: 2 }}
                  >
                    5x
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ mb: 2, lineHeight: 1.6, color: '#555' }}
                  >
                    Father-absent kids: 5x more likely to live in poverty, 2x
                    dropout risk, 2x jail likelihood
                    <Box
                      component="sup"
                      sx={{ fontSize: '0.7rem', color: 'warning.main' }}
                    >
                      ²
                    </Box>
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontStyle: 'italic', color: '#757575', mt: 2 }}
                  >
                    A stolen bond dooms futures.
                  </Typography>
                </CardContent>
              </Card>
            </Grow>
          </Grid>

          {/* Stat Card 3 */}
          <Grid item xs={12} sm={6} md={4}>
            <Grow in={showStats} timeout={1600}>
              <Card
                sx={{
                  height: '100%',
                  bgcolor: '#fff',
                  color: '#333',
                  border: '1px solid #e0e0e0',
                  transition: 'all 0.4s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 12px 32px rgba(0,0,0,0.15)',
                  },
                }}
              >
                <CardContent sx={{ p: 4, textAlign: 'center' }}>
                  <TrendingDown
                    sx={{ fontSize: 70, color: 'primary.main', mb: 2 }}
                  />
                  <Typography
                    variant="h3"
                    component="div"
                    sx={{ fontWeight: 900, color: 'secondary.main', mb: 2 }}
                  >
                    10x
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ mb: 2, lineHeight: 1.6, color: '#555' }}
                  >
                    Divorced dads: 10x higher suicide risk
                    <Box
                      component="sup"
                      sx={{ fontSize: '0.7rem', color: 'warning.main' }}
                    >
                      ³
                    </Box>
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontStyle: 'italic', color: '#757575', mt: 2 }}
                  >
                    One parent's despair echoes in a child's silence.
                  </Typography>
                </CardContent>
              </Card>
            </Grow>
          </Grid>

          {/* Stat Card 4 */}
          <Grid item xs={12} sm={6} md={4}>
            <Grow in={showStats} timeout={1900}>
              <Card
                sx={{
                  height: '100%',
                  bgcolor: '#fff',
                  color: '#333',
                  border: '1px solid #e0e0e0',
                  transition: 'all 0.4s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 12px 32px rgba(0,0,0,0.15)',
                  },
                }}
              >
                <CardContent sx={{ p: 4, textAlign: 'center' }}>
                  <AttachMoney
                    sx={{ fontSize: 70, color: 'primary.main', mb: 2 }}
                  />
                  <Typography
                    variant="h3"
                    component="div"
                    sx={{ fontWeight: 900, color: 'secondary.main', mb: 2 }}
                  >
                    $112B
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ mb: 2, lineHeight: 1.6, color: '#555' }}
                  >
                    yearly taxpayer cost from family fragmentation via welfare,
                    courts, crime
                    <Box
                      component="sup"
                      sx={{ fontSize: '0.7rem', color: 'warning.main' }}
                    >
                      ⁴
                    </Box>
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontStyle: 'italic', color: '#757575', mt: 2 }}
                  >
                    Your money funds the fallout of broken laws.
                  </Typography>
                </CardContent>
              </Card>
            </Grow>
          </Grid>

          {/* Stat Card 5 */}
          <Grid item xs={12} sm={6} md={4}>
            <Grow in={showStats} timeout={2200}>
              <Card
                sx={{
                  height: '100%',
                  bgcolor: '#fff',
                  color: '#333',
                  border: '1px solid #e0e0e0',
                  transition: 'all 0.4s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 12px 32px rgba(0,0,0,0.15)',
                  },
                }}
              >
                <CardContent sx={{ p: 4, textAlign: 'center' }}>
                  <CheckCircle
                    sx={{ fontSize: 70, color: 'primary.main', mb: 2 }}
                  />
                  <Typography
                    variant="h3"
                    component="div"
                    sx={{ fontWeight: 900, color: 'secondary.main', mb: 2 }}
                  >
                    50%
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ mb: 2, lineHeight: 1.6, color: '#555' }}
                  >
                    Shared parenting cuts conflict by up to 50% and boosts child
                    outcomes like intact families
                    <Box
                      component="sup"
                      sx={{ fontSize: '0.7rem', color: 'warning.main' }}
                    >
                      ⁵
                    </Box>
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontStyle: 'italic', color: '#757575', mt: 2 }}
                  >
                    Equity heals what bias breaks.
                  </Typography>
                </CardContent>
              </Card>
            </Grow>
          </Grid>

          {/* Stat Card 6 - Progress States */}
          <Grid item xs={12} sm={6} md={4}>
            <Grow in={showStats} timeout={2500}>
              <Card
                sx={{
                  height: '100%',
                  bgcolor: '#fff',
                  color: '#333',
                  border: '1px solid #e0e0e0',
                  transition: 'all 0.4s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 12px 32px rgba(0,0,0,0.15)',
                  },
                }}
              >
                <CardContent sx={{ p: 4, textAlign: 'center' }}>
                  <Gavel sx={{ fontSize: 70, color: 'primary.main', mb: 2 }} />
                  <Typography
                    variant="h3"
                    component="div"
                    sx={{ fontWeight: 900, color: 'secondary.main', mb: 2 }}
                  >
                    6/50
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ mb: 2, lineHeight: 1.6, color: '#555' }}
                  >
                    States with equal parenting laws see 25% divorce drops,
                    halved DV claims
                    <Box
                      component="sup"
                      sx={{ fontSize: '0.7rem', color: 'warning.main' }}
                    >
                      ⁶
                    </Box>
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontStyle: 'italic', color: '#757575', mt: 2 }}
                  >
                    Proven success in 6 states. 44 to go.
                  </Typography>
                </CardContent>
              </Card>
            </Grow>
          </Grid>
        </Grid>

        {/* Research Callout */}
        <Paper
          elevation={0}
          component={Link}
          to="/research/overnights-and-parenting-plans"
          sx={{
            mt: 4,
            p: 3,
            display: 'block',
            textDecoration: 'none',
            color: 'inherit',
            bgcolor: 'primary.main',
            borderRadius: 2,
            '&:hover': { bgcolor: 'primary.dark' },
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 700, color: 'primary.contrastText', mb: 0.5 }}
          >
            📖 Read the Full Research
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: 'primary.contrastText', opacity: 0.9 }}
          >
            What does the peer-reviewed research say about overnights and
            parenting plans for young children? Read our evidence-based summary →
          </Typography>
        </Paper>

        {/* Sources */}
        <Paper
          elevation={0}
          sx={{
            mt: 6,
            p: 3,
            bgcolor: '#f5f5f5',
            border: '1px solid #e0e0e0',
            borderRadius: 2,
          }}
        >
          <Typography
            variant="caption"
            component="div"
            sx={{ color: '#666', lineHeight: 1.8 }}
          >
            <strong>Sources:</strong> ¹U.S. Office of Juvenile Justice and
            Delinquency Prevention (ojjdp.ojp.gov); ²U.S. Census Bureau & Oregon
            Legislature (census.gov, olis.oregonlegislature.gov); ³Institute for
            Family Studies (ifstudies.org); ⁴Institute for American Values
            (healthymarriageinfo.org); ⁵American Psychological Association
            (apa.org); ⁶National Parents Organization (sharedparenting.org).
          </Typography>
        </Paper>
      </Container>

      {/* Emotional Stories Section */}
      <Box sx={{ bgcolor: '#f5f5f5', py: 8 }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            component="h2"
            align="center"
            gutterBottom
            sx={{
              fontWeight: 800,
              mb: 6,
              color: '#333',
              fontSize: { xs: '2rem', md: '3rem' },
            }}
          >
            Real Stories, Real Pain
          </Typography>

          <Grid container spacing={4}>
            {/* Story 1 */}
            <Grid item xs={12} md={4}>
              <Fade in timeout={2000}>
                <Paper
                  sx={{
                    p: 4,
                    height: '100%',
                    bgcolor: '#fff',
                    borderLeft: '4px solid',
                    borderLeftColor: 'error.main',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                >
                  <Box sx={{ position: 'relative', zIndex: 1 }}>
                    <Avatar
                      sx={{
                        width: 60,
                        height: 60,
                        mb: 2,
                        bgcolor: 'error.main',
                        fontSize: '1.5rem',
                        fontWeight: 700,
                      }}
                    >
                      M
                    </Avatar>
                    <Typography
                      variant="body1"
                      sx={{
                        fontStyle: 'italic',
                        mb: 3,
                        color: '#555',
                        fontSize: '1.1rem',
                        lineHeight: 1.7,
                      }}
                    >
                      "I fought 204 days just to hold my son again. Courts
                      turned me into a stranger."
                    </Typography>
                    <Typography
                      variant="subtitle2"
                      sx={{ color: 'warning.main', fontWeight: 700 }}
                    >
                      — Mark, Missouri Dad
                    </Typography>
                  </Box>
                </Paper>
              </Fade>
            </Grid>

            {/* Story 2 */}
            <Grid item xs={12} md={4}>
              <Fade in timeout={2300}>
                <Paper
                  sx={{
                    p: 4,
                    height: '100%',
                    bgcolor: '#fff',
                    borderLeft: '4px solid',
                    borderLeftColor: 'error.main',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                >
                  <Box sx={{ position: 'relative', zIndex: 1 }}>
                    <Avatar
                      sx={{
                        width: 60,
                        height: 60,
                        mb: 2,
                        bgcolor: 'secondary.dark',
                        fontSize: '1.5rem',
                        fontWeight: 700,
                      }}
                    >
                      A
                    </Avatar>
                    <Typography
                      variant="body1"
                      sx={{
                        fontStyle: 'italic',
                        mb: 3,
                        color: '#555',
                        fontSize: '1.1rem',
                        lineHeight: 1.7,
                      }}
                    >
                      "My kids lost their dad to 'every other weekend' – now
                      they're lost too, angry and alone."
                    </Typography>
                    <Typography
                      variant="subtitle2"
                      sx={{ color: 'warning.main', fontWeight: 700 }}
                    >
                      — Alexandra, Divorced Mom
                    </Typography>
                  </Box>
                </Paper>
              </Fade>
            </Grid>

            {/* Story 3 */}
            <Grid item xs={12} md={4}>
              <Fade in timeout={2600}>
                <Paper
                  sx={{
                    p: 4,
                    height: '100%',
                    bgcolor: '#fff',
                    borderLeft: '4px solid',
                    borderLeftColor: 'error.main',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                >
                  <Box sx={{ position: 'relative', zIndex: 1 }}>
                    <Avatar
                      sx={{
                        width: 60,
                        height: 60,
                        mb: 2,
                        bgcolor: 'primary.main',
                        fontSize: '1.5rem',
                        fontWeight: 700,
                      }}
                    >
                      S
                    </Avatar>
                    <Typography
                      variant="body1"
                      sx={{
                        fontStyle: 'italic',
                        mb: 3,
                        color: '#555',
                        fontSize: '1.1rem',
                        lineHeight: 1.7,
                      }}
                    >
                      &quot;As a child of sole custody, I grew up wondering why
                      I wasn&apos;t enough for both.&quot;
                    </Typography>
                    <Typography
                      variant="subtitle2"
                      sx={{ color: 'warning.main', fontWeight: 700 }}
                    >
                      — Sarah M., Adult Child of Divorce
                    </Typography>
                  </Box>
                </Paper>
              </Fade>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Why Every State Needs This Now */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography
          variant="h2"
          component="h2"
          align="center"
          gutterBottom
          sx={{
            fontWeight: 800,
            mb: 6,
            color: '#333',
            fontSize: { xs: '2rem', md: '3rem' },
          }}
        >
          Why Every State Needs This NOW
        </Typography>

        <Paper
          elevation={3}
          sx={{
            p: 4,
            bgcolor: '#fff',
            border: '1px solid #e0e0e0',
            borderRadius: 2,
          }}
        >
          <List>
            <ListItem sx={{ py: 2 }}>
              <ListItemIcon>
                <CheckCircle sx={{ color: 'warning.main', fontSize: 40 }} />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography
                    variant="h6"
                    sx={{ color: '#333', fontWeight: 700, mb: 1 }}
                  >
                    Ends 'winner-take-all' battles that scar kids emotionally
                  </Typography>
                }
                secondary={
                  <Typography variant="body2" sx={{ color: '#666' }}>
                    No more forcing children to choose sides in destructive
                    custody wars.
                  </Typography>
                }
              />
            </ListItem>

            <ListItem sx={{ py: 2 }}>
              <ListItemIcon>
                <CheckCircle sx={{ color: 'warning.main', fontSize: 40 }} />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography
                    variant="h6"
                    sx={{ color: '#333', fontWeight: 700, mb: 1 }}
                  >
                    Saves families from alienation and endless court fights
                  </Typography>
                }
                secondary={
                  <Typography variant="body2" sx={{ color: '#666' }}>
                    Reduces litigation costs and emotional trauma for everyone
                    involved.
                  </Typography>
                }
              />
            </ListItem>

            <ListItem sx={{ py: 2 }}>
              <ListItemIcon>
                <CheckCircle sx={{ color: 'warning.main', fontSize: 40 }} />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography
                    variant="h6"
                    sx={{ color: '#333', fontWeight: 700, mb: 1 }}
                  >
                    Cuts taxpayer waste on poverty programs and prisons
                  </Typography>
                }
                secondary={
                  <Typography variant="body2" sx={{ color: '#666' }}>
                    Saves billions by preventing the downstream costs of
                    fatherless homes.
                  </Typography>
                }
              />
            </ListItem>

            <ListItem sx={{ py: 2 }}>
              <ListItemIcon>
                <CheckCircle sx={{ color: 'warning.main', fontSize: 40 }} />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography
                    variant="h6"
                    sx={{ color: '#333', fontWeight: 700, mb: 1 }}
                  >
                    Proven: 6 states with laws see 25% divorce drops, halved DV
                    claims
                    <Box
                      component="sup"
                      sx={{ fontSize: '0.7rem', color: 'warning.main' }}
                    >
                      ⁶
                    </Box>
                  </Typography>
                }
                secondary={
                  <Typography variant="body2" sx={{ color: '#666' }}>
                    Real data from states that have already implemented these
                    reforms.
                  </Typography>
                }
              />
            </ListItem>
          </List>
        </Paper>
      </Container>
    </Box>
  )
}

export default EqualSharedParenting
